import { formatUnits } from 'ethers'

import formatDecimals from '@ambire-common/utils/formatDecimals/formatDecimals'
import {
  DcaOrder,
  DcaOrderStatus,
  DcaToken
} from '@web/extension-services/background/controllers/dca/types'

export type DcaHistoryTab = 'all' | 'active' | 'past'

/** Still running or resumable: the "Active DCAs" tab. */
export const isOpenOrder = (status: DcaOrderStatus) => status === 'ACTIVE' || status === 'PAUSED'

/**
 * Cancel is offered for every order the contract still holds as Active or
 * Paused. EXPIRED is API-only (on-chain it is still Active or Paused) and is
 * worth cancelling, because that lowers the allowance it keeps.
 */
export const isCancellable = (status: DcaOrderStatus) =>
  status === 'ACTIVE' || status === 'PAUSED' || status === 'EXPIRED'

export const filterOrdersByTab = (orders: DcaOrder[], tab: DcaHistoryTab) => {
  if (tab === 'active') return orders.filter((o) => isOpenOrder(o.status))
  if (tab === 'past') return orders.filter((o) => !isOpenOrder(o.status))
  return orders
}

export const getToken = (tokens: Record<string, DcaToken>, address: string) =>
  tokens[address.toLowerCase()] ?? null

/** Matches either token's symbol or address, or the order id. */
export const matchesSearch = (order: DcaOrder, tokens: Record<string, DcaToken>, query: string) => {
  const q = query.trim().toLowerCase()
  if (!q) return true
  return (
    [order.tokenIn, order.tokenOut].some((address) => {
      const token = getToken(tokens, address)
      return address.toLowerCase().includes(q) || !!token?.symbol.toLowerCase().includes(q)
    }) || order.orderId === q.replace(/^#/, '')
  )
}

/** 0–100, rounded down so 100% only shows once every buy ran. */
/** The API keeps a missed or failed slot's time until the keeper retries it. */
export const getUpcomingExecutionAt = (nextExecutionAt: string | null, now = Date.now()) =>
  nextExecutionAt && Date.parse(nextExecutionAt) > now ? nextExecutionAt : null

export const getProgressPercent = (executedCount: number, totalExecutions: number) =>
  totalExecutions > 0 ? Math.min(100, Math.floor((executedCount / totalExecutions) * 100)) : 0

export const getTotalBudgetRaw = (
  order: Pick<DcaOrder, 'amountPerExecutionRaw' | 'totalExecutions'>
) => BigInt(order.amountPerExecutionRaw) * BigInt(order.totalExecutions)

/**
 * Units of tokenOut received per unit of tokenIn swapped. The fee is taken
 * before the swap, so it is not part of the price (DCA client doc §3.6).
 */
export const getRate = ({
  amountInRaw,
  feeRaw,
  amountOutRaw,
  decimalsIn,
  decimalsOut
}: {
  amountInRaw: string | null
  feeRaw: string | null
  amountOutRaw: string | null
  decimalsIn: number
  decimalsOut: number
}): number | null => {
  if (!amountInRaw || !amountOutRaw) return null
  const swapped = BigInt(amountInRaw) - BigInt(feeRaw || '0')
  if (swapped <= 0n) return null
  const rate =
    Number(formatUnits(BigInt(amountOutRaw), decimalsOut)) /
    Number(formatUnits(swapped, decimalsIn))
  return Number.isFinite(rate) ? rate : null
}

export const formatRate = (rate: number | null) =>
  rate === null ? '-' : formatDecimals(rate, 'precise')

/** The code part of an execution `reason` (`CODE:DETAIL`); DETAIL is debug-only. */
export const getReasonCode = (reason: string | null) => (reason ? reason.split(':')[0]! : null)
